package dev.lingvo.site.components.marketing

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.interactiveTransition
import dev.lingvo.site.ds.pressScale
import dev.lingvo.site.ds.pressTransition
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

val AgentSetupShellStyle = CssStyle.base {
    Modifier.fillMaxWidth().styleModifier { property("min-width", "0") }
}

val AgentClientPickerStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(Space.SM).styleModifier {
            property("display", "grid")
            property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        }
    }
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") } }
}

val AgentClientChoiceStyle = CssStyle {
    base {
        Modifier.backgroundColor(colorMode.toSitePalette().surface)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Card).padding(Space.MD).gap(Space.MD)
            .fontSize(TypeScale.Small).fontWeight(600).interactiveTransition().styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("min-height", "72px")
                property("text-align", "left")
                property("font-family", "inherit")
                property("cursor", "pointer")
            }
    }
    cssRule("[aria-pressed=true]") {
        Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle)
            .color(colorMode.toSitePalette().accent)
    }
    hover { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken) }
    cssRule(":active:not(:focus-visible)") { Modifier.pressScale().pressTransition() }
    focusVisible { Modifier.styleModifier { property("transition", "none"); property("outline", "2px solid ${colorMode.toSitePalette().accent}"); property("outline-offset", "3px") } }
}

val AgentSetupTransportStyle = CssStyle.base {
    Modifier.fillMaxWidth().gap(Space.SM).styleModifier { property("display", "flex"); property("flex-wrap", "wrap") }
}

val AgentTransportChoiceStyle = CssStyle {
    base {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceBase)
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control).padding(topBottom = Space.SM, leftRight = Space.MD)
            .fontSize(TypeScale.Small).interactiveTransition().styleModifier {
                property("min-height", "40px"); property("cursor", "pointer"); property("font-family", "inherit")
            }
    }
    cssRule("[aria-pressed=true]") {
        Modifier.color(colorMode.toSitePalette().accentContrast).backgroundColor(colorMode.toSitePalette().accent)
    }
    cssRule(":disabled") { Modifier.styleModifier { property("opacity", "0.5"); property("cursor", "not-allowed") } }
    cssRule(":active:not(:disabled):not(:focus-visible)") { Modifier.pressScale().pressTransition() }
    focusVisible { Modifier.styleModifier { property("transition", "none"); property("outline", "2px solid ${colorMode.toSitePalette().accent}"); property("outline-offset", "3px") } }
}

val AgentSetupNoteStyle = CssStyle.base {
    Modifier.margin(0.px).color(colorMode.toSitePalette().textSecondary).fontSize(TypeScale.BodySmall)
        .styleModifier { property("line-height", "1.6"); property("max-width", "68ch") }
}

val AgentSetupStepsStyle = CssStyle.base {
    Modifier.fillMaxWidth().border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .backgroundColor(colorMode.toSitePalette().surface).borderRadius(Radius.Card)
        .styleModifier { property("overflow", "hidden") }
}

val AgentSetupStepStyle = CssStyle {
    base {
        Modifier.padding(Space.XL).gap(Space.MD).styleModifier {
            property("display", "grid"); property("grid-template-columns", "28px minmax(0, 1fr)")
        }
    }
    cssRule("+ .agent-setup-step") { Modifier.styleModifier { property("border-top", "1px solid ${colorMode.toSitePalette().border}") } }
}

val AgentSetupNumberStyle = CssStyle.base {
    Modifier.color(colorMode.toSitePalette().accent).fontSize(TypeScale.Small).fontWeight(600)
        .styleModifier { property("font-variant-numeric", "tabular-nums"); property("padding-top", "4px") }
}

val AgentSetupStepTitleStyle = CssStyle.base {
    Modifier.margin(0.px).displayFont().fontSize(TypeScale.HeadingSmall).fontWeight(600)
        .color(colorMode.toSitePalette().textPrimary)
}
